import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { catchError, EMPTY, ReplaySubject, switchMap, take, throwError } from 'rxjs';

let isRefreshing = false;
let tokenRefreshed$ = new ReplaySubject<string>(1); // almacena el último token emitido

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService: AuthService = inject(AuthService);

  const excludeList = ['auth/login', 'auth/refresh', 'auth/reset-password', 'auth/forgot-password'];
  if (excludeList.some(action => req.url.includes(action))) {
    return next(req);
  }

  const accessToken = authService.getAccessToken();

  if (authService.isValidToken(accessToken)) {
    const clonedRequest = req.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } });
    return next(clonedRequest);
  }

  // Token inválido: se debe refrescar
  if (!isRefreshing) {
    isRefreshing = true;
    const refreshToken = authService.getRefreshToken();

    if (!refreshToken) {
      authService.logout();
      return throwError(() => new Error('Refresh token no válido.'));
    }

    return authService.refreshToken(refreshToken).pipe(
      switchMap((res: any) => {
        if (!res?.accessToken) {
          authService.logout();
          return throwError(() => new Error('Sesión inválida.'));
        }

        authService.setTokens(res.accessToken, res.refreshToken);
        tokenRefreshed$.next(res.accessToken); // Emite el nuevo token
        tokenRefreshed$.complete(); // Completa para limpiar
        isRefreshing = false;

        const clonedRequest = req.clone({ setHeaders: { Authorization: `Bearer ${res.accessToken}` } });
        return next(clonedRequest);
      }),
      catchError((err) => {
        isRefreshing = false;
        tokenRefreshed$ = new ReplaySubject(1); // reinicia el stream
        if (err.status === 401) {
          authService.logout();
          return throwError(() => err);
        }
        return throwError(() => new Error('Error conectando con el servidor.'));
      })
    );
  } else {
    // Esperar a que el token sea refrescado
    return tokenRefreshed$.pipe(
      take(1),
      switchMap((newToken) => {
        const clonedRequest = req.clone({ setHeaders: { Authorization: `Bearer ${newToken}` } });
        return next(clonedRequest);
      })
    );
  }
};
